import {
    ASSISTANT_PLAN_STEP_STATUSES,
    ASSISTANT_TOOL_NAMES,
    ASSISTANT_TOOL_OUTPUT_STATUSES,
    ASSISTANT_TOOL_OUTPUT_TYPES,
} from "~components/outfitter-assistant/assistant-tool-constants"
import {
    getOutputRecord,
    isRecord,
    type AnyToolUIPart,
} from "~components/shared/chat/tool-ui-part-helpers"

export type AssistantPlanStepStatus =
    (typeof ASSISTANT_PLAN_STEP_STATUSES)[keyof typeof ASSISTANT_PLAN_STEP_STATUSES]

export interface AssistantPlanStep {
    readonly id: string
    readonly label: string
    readonly status: AssistantPlanStepStatus
}

export interface AssistantPlanPresentation {
    readonly title: string
    readonly description?: string
    readonly steps: readonly AssistantPlanStep[]
}

const DEFAULT_PLAN_TITLE = "Plan"
const DEFAULT_STEP_STATUS: AssistantPlanStepStatus =
    ASSISTANT_PLAN_STEP_STATUSES.PENDING

/**
 * Maps the server's step-status vocabulary onto our canonical statuses. The
 * agent emits values like "active"/"done"; keep this lenient so minor wording
 * changes don't silently drop the in-progress/completed styling.
 */
const STEP_STATUS_BY_RAW_VALUE: Readonly<
    Record<string, AssistantPlanStepStatus>
> = {
    pending: ASSISTANT_PLAN_STEP_STATUSES.PENDING,
    todo: ASSISTANT_PLAN_STEP_STATUSES.PENDING,
    not_started: ASSISTANT_PLAN_STEP_STATUSES.PENDING,
    active: ASSISTANT_PLAN_STEP_STATUSES.IN_PROGRESS,
    in_progress: ASSISTANT_PLAN_STEP_STATUSES.IN_PROGRESS,
    current: ASSISTANT_PLAN_STEP_STATUSES.IN_PROGRESS,
    completed: ASSISTANT_PLAN_STEP_STATUSES.COMPLETED,
    complete: ASSISTANT_PLAN_STEP_STATUSES.COMPLETED,
    done: ASSISTANT_PLAN_STEP_STATUSES.COMPLETED,
}

export function getAssistantPlanPresentation(
    part: AnyToolUIPart
): AssistantPlanPresentation | null {
    if (part.type !== `tool-${ASSISTANT_TOOL_NAMES.PRESENT_PLAN}`) return null

    const output = getOutputRecord(part)
    if (!isPlanProgressOutput(output)) return null

    const plan = isRecord(output.plan) ? output.plan : output
    const steps = parsePlanSteps(plan.steps)
    if (!steps.length) return null

    return {
        steps,
        title: toOptionalString(plan.title) ?? DEFAULT_PLAN_TITLE,
        description:
            toOptionalString(plan.details) ??
            toOptionalString(plan.description),
    }
}

function isPlanProgressOutput(
    output: Readonly<Record<string, unknown>> | null
): output is Readonly<Record<string, unknown>> {
    if (!output) return false

    const isPlanProgress =
        output.type === ASSISTANT_TOOL_OUTPUT_TYPES.PLAN_PROGRESS ||
        output.interactionKind === ASSISTANT_TOOL_OUTPUT_TYPES.PLAN_PROGRESS

    // A failed tool call may still carry parseable steps; don't render the card
    // for it, so the inline error message surfaces instead of a "healthy" plan.
    return (
        isPlanProgress && output.status !== ASSISTANT_TOOL_OUTPUT_STATUSES.ERROR
    )
}

function parsePlanSteps(value: unknown): AssistantPlanStep[] {
    if (!Array.isArray(value)) return []

    return value.flatMap((entry, index) => {
        if (!isRecord(entry)) return []

        const label = toOptionalString(entry.label)
        if (!label) return []

        const id = toOptionalString(entry.id) ?? `step-${index}`
        const status = parseStepStatus(entry.status)

        return [{ id, label, status }]
    })
}

function parseStepStatus(value: unknown): AssistantPlanStepStatus {
    if (typeof value !== "string") return DEFAULT_STEP_STATUS

    return (
        STEP_STATUS_BY_RAW_VALUE[value.trim().toLowerCase()] ??
        DEFAULT_STEP_STATUS
    )
}

function toOptionalString(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() ? value.trim() : undefined
}
